import resetStore from '@/helpers/reset-store';
import axios from 'axios';

export const state = () => ({
  products: [],
  items: [],
  savedBaskets: [],
  loading: false,
  initialized: false
});

export const mutations = {
  RESET_STORE: resetStore(state),
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_INITIALIZED(state, initialized) {
    state.initialized = initialized;
  },
  SET_SAVED_BASKETS(state, baskets) {
    state.savedBaskets = baskets;
    state.initialized = true;
    state.loading = false;
  },
  UPDATE_BASKET_NAME(state, { basketID, basketName }) {
    const basket = state.savedBaskets.find(b => b.id === basketID);
    if (basket) basket.basket_name = basketName;
  },
  DELETE_BASKET(state, basketID) {
    state.savedBaskets = state.savedBaskets.filter(b => b.id !== basketID);
  },
  UPDATE_CHECKED_BASKET(state, basketID) {
    const basket = state.savedBaskets.find(b => b.id === basketID);
    if (basket) basket.checked_out = 0;
  }
};

export const getters = {
  products: state => state.products,
  items: state => state.items,
  savedBaskets: state => state.savedBaskets,
  loading: state => state.loading,
  initialized: state => state.initialized
};

export const actions = {
  async fetchBaskets({ commit }, justBaskets = false) {
    commit('SET_LOADING', true);
    try {
      const response = await axios.get('/api/saved-basket', {
        params: {
          justBaskets,
          includeBudget: true
        }
      });
      const baskets = response.data.data || response.data; // Adjust according to your API response
      commit('SET_SAVED_BASKETS', baskets);
    } catch (error) {
      console.error('Error fetching baskets:', error);
      commit('SET_LOADING', false);
      commit('SET_INITIALIZED', true);
    }
  },
  async addNewSavedBasket({ commit }, { basketData, newBasketName }) {
    try {
      const { data } = await axios.post('/api/saved-basket', { items: basketData, newBasketName });
      commit('SET_SAVED_BASKETS', data);
    } catch (error) {
      console.error('Error adding new basket:', error);
    }
  },
  // In src/store/saved-baskets.js -> actions

async approveAndMoveToBasket({ commit, dispatch }, savedBasket) {

  try {
    // Determine if this is a manpack order
    const isManpack = savedBasket.order_type === 'manpack';

    if (isManpack) {
      // Handle manpack basket approval
      // 1. Clear the user's current manpack baskets
      await dispatch('basket/removeBasket', 'manpack', { root: true });

      // 2. Reconstruct manpack structure from saved basket items
      // Group items by contact_id to create packs
      const packsMap = {};
      savedBasket.itemsArray.forEach(item => {
        const contactId = item.contact_id;
        if (!packsMap[contactId]) {
          packsMap[contactId] = [];
        }
        packsMap[contactId].push({
          parentId: item.parent?.internalId || item.internalId,
          price: parseFloat(item.price),
          productId: item.internalId,
          qty: item.qty,
          name: item.name,
          thumbnail: item.thumbnail
        });
      });

      // 3. Add each pack to the manpack basket
      for (const contactId in packsMap) {
        await dispatch('manpack/selectEmployee', contactId, { root: true });
        const addPromises = packsMap[contactId].map(item => {
          return dispatch('basket/addToBasket', item, { root: true });
        });
        await Promise.all(addPromises);
      }

      commit('basket/SET_BASKET_ID_TO_APPROVE', savedBasket.id, { root: true });
      return { success: true };
    } else {
      // Handle stock basket approval
      // Get all items - include inactive/discontinued items so they show with "unavailable" notice
      const allItems = savedBasket.itemsArray || [];
      const inactiveItems = allItems.filter(item =>
        item.isInactive === true ||
        item.custitem_smi_mysmi_remove_item?.value === true
      );

      // Log if any inactive items are present
      if (inactiveItems.length > 0) {
        console.warn(`[approveAndMoveToBasket] Basket contains ${inactiveItems.length} inactive item(s):`,
          inactiveItems.map(i => i.itemId || i.internalId));
      }

      // 1. Clear the user's current stock basket
      await dispatch('basket/removeBasket', 'stock', { root: true });

      // 2. Commit the address ID from the saved basket to the store for the checkout page
      if (savedBasket.address_id) {
        console.log(`[Vuex Action] Found address_id: ${savedBasket.address_id}. Committing to store.`);
        commit('basket/SET_APPROVED_ORDER_ADDRESS_ID', savedBasket.address_id, { root: true });
      } else {
        console.error('[Vuex Action] ERROR: The savedBasket object passed to this action is missing the address_id field!');
      }

      // 3. Add ALL items from the approved basket to the main basket (including inactive ones)
      const addPromises = allItems.map(item => {
        const basketObject = {
          parentId: item.parent?.internalId || item.internalId,
          price: parseFloat(item.price),
          productId: item.internalId,
          qty: item.qty,
          isInactive: item.isInactive === true || item.custitem_smi_mysmi_remove_item?.value === true
        };
        return dispatch('basket/addToBasket', basketObject, { root: true });
      });
      await Promise.all(addPromises);
      commit('basket/SET_BASKET_ID_TO_APPROVE', savedBasket.id, { root: true });
      return { success: true, inactiveCount: inactiveItems.length };
    }
  } catch (error) {
    console.error("Error during basket approval:", error);
    return { success: false, error };
  }
},
  async loadBasketForCheckout({ dispatch, commit }, savedBasket) {
    try {
      // Get all items - include inactive items so they show with "unavailable" notice
      const allItems = savedBasket.itemsArray || [];
      const inactiveItems = allItems.filter(item =>
        item.isInactive === true ||
        item.custitem_smi_mysmi_remove_item?.value === true
      );

      // Log if any inactive items are present
      if (inactiveItems.length > 0) {
        console.warn(`[loadBasketForCheckout] Basket contains ${inactiveItems.length} inactive item(s):`,
          inactiveItems.map(i => i.itemId || i.internalId));
      }

      // 1. Clear the user's current stock basket
      await dispatch('basket/removeBasket', 'stock', { root: true });

      // 2. Add ALL items from the basket to the main basket (including inactive ones)
      const addPromises = allItems.map(item => {
        const basketObject = {
          parentId: item.parent?.internalId || item.internalId,
          price: parseFloat(item.price),
          productId: item.internalId,
          qty: item.qty,
          name: item.name,
          thumbnail: item.thumbnail,
          isInactive: item.isInactive === true || item.custitem_smi_mysmi_remove_item?.value === true
        };
        return dispatch('basket/addToBasket', basketObject, { root: true });
      });
      await Promise.all(addPromises);

      // 3. IMPORTANT: Instead of updating the DB, we now commit the ID to the basket state
      commit('basket/SET_BASKET_ID_TO_APPROVE', savedBasket.id, { root: true });

      return { success: true, inactiveCount: inactiveItems.length };
    } catch (error) {
      console.error("Error loading basket for checkout:", error);
      return { success: false, error };
    }
  },
  async deleteBasket({ commit }, basketID) {
    try {
      await axios.patch('/api/saved-basket', { deleteBasket: true, basketID });
      commit('DELETE_BASKET', basketID);
    } catch (error) {
      console.error('Error deleting basket:', error);
    }
  },

  async updateCheckedOutBasket({ commit }, basketID) {
    try {
      await axios.patch('/api/saved-basket', { basket: basketID, checked_out: 1 });
      commit('RESET_STORE');
    } catch (error) {
      console.error('Error updating basket status:', error);
    }
  },

  async updatePrice({ commit }, { basket, productId }) {
    try {
      const { data } = await axios.patch('/api/saved-basket', { basket_id: basket, item_id: productId });
      commit('SET_SAVED_BASKETS', data);
    } catch (error) {
      console.error('Error updating price:', error);
    }
  },

  async removeFromSavedBasket({ commit }, { basket, productId }) {
    try {
      await axios.patch('/api/saved-basket', { basket_id: basket, item_id: productId, delete_from_saved: true });
      // Don't commit here - let the caller fetch fresh data like variant update does
    } catch (error) {
      console.error('Error removing item from basket:', error);
      throw error; // Re-throw so caller knows it failed
    }
  },

  async updateQuantity({ commit }, { basket, productId, qty }) {
    try {
      const { data } = await axios.patch('/api/saved-basket', {
        basket_id: basket,
        item_id: productId,
        qty: qty
      });
      commit('SET_SAVED_BASKETS', data);
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};